import React, { useEffect } from "react";
import type { SelectProps } from "antd";
import {
  Input,
  Form,
  Button,
  Divider,
  Select,
  message,
  Modal,
  Row,
  Col,
  InputNumber,
  DatePicker,
  Tag,
  Space,
} from "antd";
import styles from "../maintenance.module.scss";
import { useTranslation } from "react-i18next";
import { CharacterVerify } from "../../../Utility/RegExp";
import type { RangePickerProps } from "antd/es/date-picker";
import moment from "moment";

import { ticketRecordProps } from "../index";
import { useUpdateTicket } from "../queries";
import { useIdentity } from "Components/Topology/queries";
import { useUser } from "../../Management/queries";
import { useAppSelector } from "store/hook";

import { selectOption } from "./CreateTicket_Modal";

interface editInfoProps {
  editTicketVisible: boolean;
  setEditTicketVisible: (arg: boolean) => void;
  // record: ticketRecordProps;
  // setRecord?: (arg: ticketRecordProps | any) => void;
}

const EditTicket_Modal = React.memo(({ editTicketVisible, setEditTicketVisible }: editInfoProps) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { mutateAsync: updateTicketMutation, isLoading } = useUpdateTicket();
  const { loginCid, stateCid, level, isLogin } = useAppSelector((state) => state.global);
  const { record } = useAppSelector((state) => state.goPage);
  // const { deviceTableData } = useAppSelector((state) => state.topology);
  const { data, isLoading: loading, isFetching } = useIdentity(level, stateCid, loginCid);
  const { data: users } = useUser(stateCid, level, level === "super_super" || level === "super" || level === "admin");

  const selectProps: SelectProps = {
    mode: "multiple",
    style: { width: "100%" },
    options: selectOption(data?.response?.device_identity),
    maxTagCount: "responsive",
  };

  const disabledDate: RangePickerProps["disabledDate"] = (current) => {
    // Can not select days before today and today
    return current < moment().subtract(1, "days").endOf("day");
  };

  const updateTicket = (values: any) => {
    updateTicketMutation(values)
      .then(() => {
        setEditTicketVisible(false);
        form.resetFields();
        message.success(t("ISMS.Success"));
      })
      .catch(() => {
        message.error(t("ISMS.Fail"));
      });
  };

  useEffect(() => {
    if (record.list) {
      const date = moment(record.list.expire_utc * 1000).format("YYYY-MM-DD HH:mm:ss");

      form.setFieldsValue({
        _id: record.list._id,
        cid: record.cid,
        ticket: record.list.ticket,
        ticketNumber: record.list.ticket?.slice(0, 14),
        ticket_type: record.list?.ticket_type?.charAt(0).toUpperCase() + record.list?.ticket_type?.slice(1),
        description: record.list.desc,
        token: record.list.url_token,
        new_no: record.list.new_no ?? 0,
        fix_id: record.list.fix_id,
        prefix_url: record.list.url_pre_fix,
        expiration: moment(`${date}`),
        active: record.list.active,
      });
    }
  }, [record.list]);

  return (
    <Modal
      title={t("ISMS.EditTicket")}
      visible={editTicketVisible}
      className={`${styles.modal}`}
      onCancel={() => {
        setEditTicketVisible(false);
        form.resetFields();
      }}
      footer={[
        <Button
          style={{ marginRight: 10 }}
          onClick={() => {
            setEditTicketVisible(false);
            form.resetFields();
          }}
        >
          {t("ISMS.Cancel")}
        </Button>,
        <Button
          key="Submit"
          loading={isLoading}
          type="primary"
          onClick={() => {
            form.submit();
          }}
        >
          {t("ISMS.Submit")}
        </Button>,
      ]}
    >
      <Form layout="vertical" name="CreateInfo" onFinish={updateTicket} form={form}>
        <div className="formwrap">
          <Row gutter={24} justify="space-around">
            <Col xs={24} sm={24} md={24} lg={24} xl={10}>
              <h2>{t("ISMS.TicketInfo")}</h2>
              {/* <Row>
                <Col>
                  <h2>{t("ISMS.TicketInfo")}</h2>
                </Col>
                <Col>
                  <Tag color="#108ee9" style={{ marginLeft: "10px", marginTop: "2px" }}>
                    {record.list?.ticket_type.charAt(0).toUpperCase() + record.list?.ticket_type.slice(1)}
                  </Tag>
                </Col>
              </Row> */}
              <Divider className={styles.divider} />
              <Form.Item name={"_id"} label={t("ISMS._id")} hidden={true}>
                <Input />
              </Form.Item>
              <Form.Item name={"cid"} label={t("ISMS.Cid")} hidden={true}>
                <Input />
              </Form.Item>
              <Form.Item name={"active"} label={t("ISMS.active")} hidden={true}>
                <Input />
              </Form.Item>
              <Form.Item name={"token"} label={t("ISMS.Token")} hidden={true}>
                <Input placeholder={""} maxLength={30} />
              </Form.Item>
              <Form.Item name={"ticket"} hidden={true}>
                <Input />
              </Form.Item>
              <Form.Item name={"ticketNumber"} label={t("ISMS.Ticket")}>
                <Input placeholder={""} maxLength={30} disabled />
              </Form.Item>
              <Form.Item name={"ticket_type"} label={t("ISMS.TicketType")}>
                <Input placeholder={""} maxLength={30} disabled />
              </Form.Item>
              <Form.Item name={"description"} label={t("ISMS.Description")}>
                <Input placeholder={""} maxLength={30} />
              </Form.Item>

              {record.list?.ticket_type === "installation" ? (
                <Form.Item name={"new_no"} label={t("ISMS.DeviceQuantity")} initialValue={0}>
                  <InputNumber placeholder={""} maxLength={30} style={{ width: "100%" }} />
                </Form.Item>
              ) : (
                <></>
              )}

              {record.list?.ticket_type === "inspection" ? (
                <Form.Item
                  name={"fix_id"}
                  label={t("ISMS.DeviceId")}
                  rules={[{ required: true, message: "Deivce Id is required!" }]}
                >
                  <Select allowClear {...selectProps} />
                </Form.Item>
              ) : (
                <></>
              )}

              <Form.Item
                name={"prefix_url"}
                label={t("ISMS.PrefixURL")}
                initialValue="http://192.168.1.1"
                hidden={true}
              >
                <Input placeholder={""} maxLength={30} />
              </Form.Item>

              <Form.Item
                name={"expiration"}
                label={t("ISMS.Expiration")}
                rules={[{ required: true, message: "Expiration date is required!" }]}
              >
                <DatePicker
                  placeholder={""}
                  style={{ width: "100%" }}
                  showToday
                  format="YYYY-MM-DD"
                  disabledDate={disabledDate}
                />
              </Form.Item>
            </Col>

            {/* <Col xs={24} sm={24} md={24} lg={24} xl={10}>
              <h2>{t("ISMS.Scheme")}</h2>
              <Divider className={styles.divider} />

              <Form.Item name={"mail"} rules={[{ type: "email", message: "Invalid!!" }]} label={t("ISMS.Device")}>
                <Input placeholder={t("ISMS.Device")} maxLength={30} />
              </Form.Item>

              <Form.Item
                label={t("ISMS.PeriodAlive(s)")}
                name="period_alive"
                rules={[{ required: true, message: "Required!!" }]}
                initialValue={30}
              >
                <Input
                  style={{ width: "100%" }}
                  min={30}
                  step={30}
                  max={86400}
                  placeholder={t("ISMS.AlivePeriodstepby30")}
                  // onChange={aliveNumberChange}
                />
              </Form.Item>
              <Form.Item
                label={t("ISMS.StatusPeriod(s)")}
                name="period_status"
                rules={[{ required: true, message: "Required!!" }]}
                initialValue={30}
              >
                <InputNumber
                  placeholder={t("ISMS.StatusPeriod")}
                  min={30}
                  step={30}
                  max={86400}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item
                label={t("ISMS.IoTPeriod(s)")}
                name="period_iot"
                rules={[{ required: true, message: "Required!!" }]}
                initialValue={30}
              >
                <InputNumber
                  placeholder={t("ISMS.IoTPeriod")}
                  min={30}
                  step={30}
                  max={86400}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item
                label={t("ISMS.GPSPeriod(s)")}
                name="period_gps"
                rules={[{ required: true, message: "Required!!" }]}
                initialValue={30}
              >
                <InputNumber
                  placeholder={t("ISMS.GPSPeriod")}
                  min={30}
                  step={30}
                  max={86400}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col> */}
          </Row>
        </div>
      </Form>
    </Modal>
  );
});

export default EditTicket_Modal;
